import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Card, CardContent, Chip, 
  Button, CircularProgress, Alert, Container, Divider, Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/auth';
import GavelIcon from '@mui/icons-material/Gavel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export default function MyArbitrations() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningRequest, setActioningRequest] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        fetchRooms(),
        fetchRequests()
      ]);
    } catch (err) {
      console.error(err);
      setError('Failed to load arbitrator dashboard details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    const { data } = await apiClient.get('/api/v1/arbitrations/mine/');
    setRooms(data.rooms || []);
  };

  const fetchRequests = async () => {
    const { data } = await apiClient.get('/api/v1/arbitrations/requests/');
    setRequests(data.requests || []);
  };

  const handleAcceptRequest = async (requestId) => {
    setError('');
    setActioningRequest(requestId);
    try {
      const { data } = await apiClient.post(`/api/v1/arbitrations/requests/${requestId}/accept/`);
      // Refresh requests and active rooms
      await Promise.all([fetchRequests(), fetchRooms()]);
      navigate(`/arbitrations/${data.arbitration_id}`);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error?.message || 'Failed to accept case request.';
      setError(msg);
    } finally {
      setActioningRequest(null);
    }
  };

  const handleDeclineRequest = async (requestId) => {
    setError('');
    setActioningRequest(requestId);
    try {
      await apiClient.post(`/api/v1/arbitrations/requests/${requestId}/decline/`);
      await fetchRequests();
    } catch (err) {
      console.error(err);
      setError('Failed to decline case request.');
    } finally {
      setActioningRequest(null);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ACTIVE': return 'warning';
      case 'CLOSED': return 'default';
      case 'RESOLVED': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#080d1a', color: '#f8fafc', py: 6 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 2, border: '1px solid rgba(99,102,241,0.2)' }}>
            <GavelIcon sx={{ color: '#818cf8', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
              Arbitrator Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Manage pending dispatch requests and active rooms. Manage your availability from the profile section in the top right corner.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4, bgcolor: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
            {error}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 4, bgcolor: 'rgba(16,185,129,0.1)', color: '#a7f3d0', border: '1px solid rgba(16,185,129,0.2)' }}>
            {successMsg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#818cf8' }} />
          </Box>
        ) : (
          <Grid container spacing={4}>
            {/* Case Requests & Active Rooms (Full Width) */}
            <Grid item xs={12}>
              <Stack spacing={4}>
                {/* Pending Case Requests Panel */}
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OfflineBoltIcon sx={{ color: '#eab308' }} /> Pending Case Requests
                  </Typography>

                  {requests.length === 0 ? (
                    <Card sx={{ bgcolor: 'rgba(13,21,38,0.4)', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 3, py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        No pending case dispatch requests. When disputes are escalated, they will appear here.
                      </Typography>
                    </Card>
                  ) : (
                    <Stack spacing={2}>
                      {requests.map((req) => (
                        <Card key={req.id} sx={{ bgcolor: 'rgba(13,21,38,0.9)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 3 }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                              <Box>
                                <Chip label="DISPATCH REQUEST" color="warning" size="small" sx={{ fontWeight: 700, borderRadius: 1.5, mb: 1 }} />
                                <Typography variant="h6" fontWeight={700}>
                                  {req.deal_title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  Deal #{req.deal_id} • Value: Rs.{parseFloat(req.deal_value).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                Received: {new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>

                            <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2, mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ color: '#818cf8', fontWeight: 600, mb: 0.5 }}>Dispute Reason:</Typography>
                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                                {req.dispute_reason}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleDeclineRequest(req.id)}
                                disabled={actioningRequest !== null}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Decline
                              </Button>
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={actioningRequest === req.id ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckCircleOutlineIcon />}
                                onClick={() => handleAcceptRequest(req.id)}
                                disabled={actioningRequest !== null}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Accept & Open Case
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Assigned Arbitration Rooms */}
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2.5 }}>
                    Assigned Arbitration Rooms ({rooms.length})
                  </Typography>

                  {rooms.length === 0 ? (
                    <Card sx={{ bgcolor: 'rgba(13,21,38,0.4)', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 3, py: 6, textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ color: '#94a3b8', mb: 0.5 }}>No rooms assigned</Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Accept a pending case request to create and enter an arbitration room.
                      </Typography>
                    </Card>
                  ) : (
                    <Grid container spacing={3}>
                      {rooms.map((room) => (
                        <Grid item xs={12} md={6} key={room.id}>
                          <Card sx={{ 
                            bgcolor: 'rgba(13,21,38,0.8)', 
                            border: '1px solid rgba(255,255,255,0.08)', 
                            borderRadius: 3,
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'rgba(129,140,248,0.3)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                            }
                          }}>
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ color: '#818cf8', fontWeight: 600, mb: 0.5 }}>
                                    ROOM #{room.id}
                                  </Typography>
                                  <Typography variant="h6" fontWeight={700}>
                                    Dispute on Deal #{room.deal_id}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={room.status} 
                                  color={getStatusColor(room.status)}
                                  size="small"
                                  sx={{ fontWeight: 600, borderRadius: 1.5 }} 
                                />
                              </Box>

                              <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />

                              <Box sx={{ display: 'flex', alignItems: 'center', color: '#94a3b8', mb: 3 }}>
                                <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                                <Typography variant="body2">
                                  Opened: {new Date(room.opened_at).toLocaleDateString()}
                                </Typography>
                              </Box>

                              <Button 
                                fullWidth
                                variant="contained" 
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate(`/arbitrations/${room.id}`)}
                                sx={{ 
                                  bgcolor: '#4f46e5', 
                                  py: 1.2,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: '#4338ca' } 
                                }}
                              >
                                Enter Room
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
}
